import { useQuery } from "@apollo/client";
import { Sangha, SanghaType } from "@app/shared/types";
import { Button, CircularProgress, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { analyticsTrack } from "app/analytics/track";
import { GRAPHQL_QUERY_PUBLICALLY_AVAILABLE_INTEREST_GROUPS } from "app/queries";
import { routes } from "app/routes";
import { GenericErrorMessage } from "components/GenericErrorMessage";
import { NavLink } from "features/navigation/NavLink";
import { usePreferredGroup } from "hooks/usePreferredGroup";
import { useUserTimezone } from "hooks/useUserTimezone";
import _ from "lodash";
import { DateTime } from "luxon";
import { useHistory, useLocation } from "react-router";
import { groupMatchesTimeOfDay, TimeOfDayOptions } from "./filters/filterHelpers";
import { getWeekDayIndex } from "./filters/timeFilterOptions";
import { FilterData } from "./filters/Filter";
import InterestGroupCard from "./InterestGroupCard";
import { SELECTED_INTEREST_GROUP_FIELD } from "./constants";
import { isUserInSetupFlow } from "features/signup/signupHelpers";
import { FixedCTAContainer } from "features/signup/FixedCTAContainer";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const useStyles = makeStyles((theme) => ({
    availableSpotsButton: {
        height: "67px",
        [theme.breakpoints.up("sm")]: {
            marginRight: theme.spacing(3),
        },
    },
    buttonContainer: {
        textAlign: "center",
        marginTop: theme.spacing(6),
        marginBottom: theme.spacing(2),
    },
    cardContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        gap: 22,
        [theme.breakpoints.up("md")]: {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        [theme.breakpoints.up("lg")]: {
            gridTemplateColumns: "repeat(3, 1fr)",
        },
    },
    surveyCard: {
        backgroundColor: theme.palette.primary200,
        color: theme.palette.primary500,
        borderRadius: theme.borderRadius.default,
        boxShadow: theme.shadow.default,
        padding: theme.spacing(0, 3),
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: theme.spacing(2),
        cursor: "pointer",
        minHeight: 310,

        "&:hover": {
            boxShadow: theme.shadow.spread,
        },
    },
    surveyContactText: {
        display: "flex",
        alignItems: "center",
    },
}));

interface InterestGroupListProps {
    groupIdsToExclude?: string[];
    filters?: FilterData;
    resetFilters: VoidFunction;
    setGroupCount: (count: number) => void;
}

export const InterestGroupList = (props: InterestGroupListProps) => {
    const history = useHistory();
    const classes = useStyles();
    const timezone = useUserTimezone();

    const { groupIdsToExclude, filters, resetFilters, setGroupCount } = props;

    const preferredGroupId = usePreferredGroup();

    const location = useLocation();
    const isSetupFlow = isUserInSetupFlow(location);
    const preselectedInterestGroup = sessionStorage.getItem(SELECTED_INTEREST_GROUP_FIELD);

    const {
        data: groupData,
        loading: groupsLoading,
        error: groupsError,
    } = useQuery(GRAPHQL_QUERY_PUBLICALLY_AVAILABLE_INTEREST_GROUPS);

    if (groupsError) {
        return <GenericErrorMessage />;
    }

    let allGroups: Sangha[] | undefined = groupData?.availableInterestGroups;

    if (groupsLoading || !allGroups) {
        return <CircularProgress />;
    }

    // List preferred group on top
    const preferredGroups = allGroups.filter((x) => x.id === preferredGroupId);
    if (preferredGroups.length > 0) {
        allGroups = [...preferredGroups, ...allGroups.filter((x) => x.id !== preferredGroupId)];
    }

    if (groupIdsToExclude) {
        allGroups = allGroups.filter((x) => !groupIdsToExclude.includes(x.id));
    }

    const isGroupClosed = (group: Sangha) => group.availableSpots <= 0;
    const isGroupOpen = (group: Sangha) => group.availableSpots > 0;

    let filteredGroups = [...allGroups];

    const allOpenGroups = allGroups.filter(isGroupOpen);

    if (filters) {
        const { status, topic, timeOfDay, dayOfWeek } = filters;
        if (status && status.length > 0) {
            filteredGroups = filteredGroups.filter(
                (x) =>
                    (filters.status.includes("OPEN") && isGroupOpen(x)) ||
                    (filters.status.includes("CLOSED") && isGroupClosed(x)),
            );
        }

        if (topic && topic.length > 0) {
            filteredGroups = filteredGroups.filter(
                (x) => x.topicData && _.includes(filters.topic, x.topicData.id),
            );
        }

        if (timeOfDay && timeOfDay.length > 0) {
            filteredGroups = filteredGroups.filter((x) =>
                _.some(timeOfDay, (timeOfDayItem) =>
                    groupMatchesTimeOfDay(x, timeOfDayItem as TimeOfDayOptions, timezone, true),
                ),
            );
        }

        if (dayOfWeek && dayOfWeek.length > 0) {
            const weekdayIndexes = _.compact(dayOfWeek.map((dayName) => getWeekDayIndex(dayName)));
            filteredGroups = filteredGroups.filter((x) =>
                _.includes(weekdayIndexes, DateTime.fromISO(x.schedule.firstSessionDate).weekday),
            );
        }
    }

    if (isSetupFlow && preselectedInterestGroup) {
        filteredGroups = filteredGroups.filter((x) => x.id === preselectedInterestGroup);
    }

    // Sort groups by those with the most available spots on top
    filteredGroups = filteredGroups.sort((a, b) => a.availableSpots - b.availableSpots);

    const renderGroups = () => {
        if (filteredGroups.length < 1) {
            return (
                <>
                    <Typography variant="body1">
                        No available groups found.{" "}
                        {filters && (
                            <>
                                Please try adjusting your filters or{" "}
                                <NavLink to={routes.contactUs()}>get in touch with us</NavLink>.
                            </>
                        )}
                    </Typography>
                </>
            );
        } else {
            return filteredGroups.map((group) => (
                <InterestGroupCard key={group.id} interestGroup={group} />
            ));
        }
    };

    setGroupCount(filteredGroups.length);

    const renderButtons = () => {
        if (isSetupFlow && preselectedInterestGroup) {
            return (
                <FixedCTAContainer>
                    <Button
                        variant="secondary"
                        data-testid="chooseAnotherGroupButton"
                        onClick={() => {
                            sessionStorage.removeItem(SELECTED_INTEREST_GROUP_FIELD);
                            resetFilters();
                        }}
                    >
                        Choose a different group
                    </Button>
                    <Button
                        variant="primary"
                        data-testid="continueWithGroupButton"
                        onClick={() => {
                            analyticsTrack("Button Clicked", {
                                label: "Continue with this group",
                                type: SanghaType.InterestGroup,
                                location: "InterestGroupList",
                            });
                            history.push(
                                routes.setupConfirmInterestGroup(preselectedInterestGroup),
                            );
                        }}
                    >
                        Continue with this group
                    </Button>
                </FixedCTAContainer>
            );
        } else {
            return (
                filteredGroups.length < allOpenGroups.length && (
                    <div className={classes.buttonContainer}>
                        <Button
                            variant="primary"
                            onClick={() => {
                                analyticsTrack("Button Clicked", {
                                    label: "Show me all groups",
                                    type: SanghaType.InterestGroup,
                                    location: "InterestGroupList",
                                });
                                resetFilters();
                            }}
                        >
                            Show me all groups
                        </Button>
                    </div>
                )
            );
        }
    };

    const renderSurveyCard = () => {
        return (
            <div
                className={classes.surveyCard}
                onClick={() => {
                    window.open("https://cloudsangha.typeform.com/to/x7fuvpp4", "_blank");
                }}
            >
                <Typography variant="h3">Can’t find a day and time that works for you?</Typography>
                <Typography variant="body1" className={classes.surveyContactText}>
                    Join the waitlist <ChevronRightIcon />
                </Typography>
            </div>
        );
    };

    return (
        <>
            <div className={classes.cardContainer}>
                {renderGroups()}
                {filteredGroups.length > 0 && renderSurveyCard()}
            </div>
            {renderButtons()}
        </>
    );
};
